import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Layout } from '../components/Layout';
import { 
  Rocket, Package, Zap, TrendingUp, Plus, Edit2, Trash2, 
  CheckCircle2, Clock, AlertCircle, Play, Pause, Settings,
  ChevronRight, BarChart3, Target, Layers, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';

const API = process.env.REACT_APP_BACKEND_URL;

const PlatformDashboard = () => {
  const [features, setFeatures] = useState([]);
  const [phases, setPhases] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Dialog states
  const [featureDialog, setFeatureDialog] = useState({ open: false, mode: 'create', data: null });
  const [phaseDialog, setPhaseDialog] = useState({ open: false, mode: 'create', data: null });
  const [automationDialog, setAutomationDialog] = useState({ open: false, mode: 'create', data: null });

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        console.log('No token found, will retry...');
        setLoading(false);
        return false;
      }
      const headers = { Authorization: `Bearer ${token}` };
      
      const [featuresRes, phasesRes, automationsRes] = await Promise.all([
        axios.get(`${API}/api/platform/features`, { headers }),
        axios.get(`${API}/api/platform/phases`, { headers }),
        axios.get(`${API}/api/platform/automations`, { headers })
      ]);
      
      setFeatures(featuresRes.data);
      setPhases(phasesRes.data);
      setAutomations(automationsRes.data);
      return true;
    } catch (error) {
      console.error('Platform data fetch error:', error);
      if (error.response?.status !== 401) {
        toast.error('Failed to load platform data');
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Try to fetch data, retry a few times if token not ready
    let retries = 0;
    const maxRetries = 3;
    
    const tryFetch = async () => {
      const success = await fetchData();
      if (!success && retries < maxRetries) {
        retries++;
        setTimeout(tryFetch, 500);
      }
    };
    
    tryFetch();
  }, [fetchData]);

  // Stats calculations
  const stats = {
    featuresComplete: features.filter(f => f.status === 'complete').length,
    featuresInProgress: features.filter(f => f.status === 'in_progress').length,
    featuresPlanned: features.filter(f => f.status === 'planned').length,
    buildProgress: features.length > 0 
      ? Math.round((features.filter(f => f.status === 'complete').length / features.length) * 100) 
      : 0,
    activeAutomations: automations.filter(a => a.status === 'active').length,
    automationSuccessRate: automations.reduce((acc, a) => acc + (a.total_runs || 0), 0) > 0
      ? Math.round((automations.reduce((acc, a) => acc + (a.successful_runs || 0), 0) / 
          automations.reduce((acc, a) => acc + (a.total_runs || 0), 0)) * 100)
      : 100,
    currentPhase: phases.find(p => p.status === 'active')
  };

  // Feature CRUD
  const handleFeatureSave = async (formData) => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      
      if (featureDialog.mode === 'create') {
        await axios.post(`${API}/api/platform/features`, formData, { headers });
        toast.success('Feature created');
      } else {
        await axios.put(`${API}/api/platform/features/${featureDialog.data.id}`, formData, { headers });
        toast.success('Feature updated');
      }
      
      setFeatureDialog({ open: false, mode: 'create', data: null });
      fetchData();
    } catch (error) {
      toast.error('Failed to save feature');
    }
  };

  const handleFeatureDelete = async (id) => {
    if (!window.confirm('Delete this feature?')) return;
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/api/platform/features/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Feature deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete feature');
    }
  };

  // Phase CRUD
  const handlePhaseSave = async (formData) => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      
      if (phaseDialog.mode === 'create') {
        await axios.post(`${API}/api/platform/phases`, formData, { headers });
        toast.success('Phase created');
      } else {
        await axios.put(`${API}/api/platform/phases/${phaseDialog.data.id}`, formData, { headers });
        toast.success('Phase updated');
      }
      
      setPhaseDialog({ open: false, mode: 'create', data: null });
      fetchData();
    } catch (error) {
      toast.error('Failed to save phase');
    }
  };

  // Automation CRUD
  const handleAutomationSave = async (formData) => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      
      if (automationDialog.mode === 'create') {
        await axios.post(`${API}/api/platform/automations`, formData, { headers });
        toast.success('Automation created');
      } else {
        await axios.put(`${API}/api/platform/automations/${automationDialog.data.id}`, formData, { headers });
        toast.success('Automation updated');
      }
      
      setAutomationDialog({ open: false, mode: 'create', data: null });
      fetchData();
    } catch (error) {
      toast.error('Failed to save automation');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'complete': return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'in_progress': return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'active': return <Play className="h-4 w-4 text-green-500" />;
      case 'inactive': return <Pause className="h-4 w-4 text-slate-500" />;
      case 'blocked': return <AlertCircle className="h-4 w-4 text-red-500" />;
      default: return <Clock className="h-4 w-4 text-slate-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      complete: 'bg-green-500/20 text-green-400 border-green-500/30',
      in_progress: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      planned: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
      active: 'bg-green-500/20 text-green-400 border-green-500/30',
      inactive: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
      blocked: 'bg-red-500/20 text-red-400 border-red-500/30',
      testing: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
    };
    return variants[status] || variants.planned;
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      P0: 'bg-red-500/20 text-red-400 border-red-500/30',
      P1: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      P2: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      P3: 'bg-slate-500/20 text-slate-400 border-slate-500/30'
    };
    return variants[priority] || variants.P2;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Rocket className="h-6 w-6 text-cyan-500" />
              Platform Dashboard
            </h1>
            <p className="text-muted-foreground text-sm">Manage features, phases, and automations</p>
          </div>
          <Button onClick={fetchData} variant="outline" size="sm" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase">Build Progress</p>
                <p className="text-2xl font-bold text-cyan-400">{stats.buildProgress}%</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-cyan-500/20 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-cyan-500" />
              </div>
            </div>
            <Progress value={stats.buildProgress} className="mt-2 h-1" />
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase">Features</p>
                <p className="text-2xl font-bold">
                  <span className="text-green-400">{stats.featuresComplete}</span>
                  <span className="text-slate-600 mx-1">/</span>
                  <span className="text-yellow-400">{stats.featuresInProgress}</span>
                  <span className="text-slate-600 mx-1">/</span>
                  <span className="text-slate-400">{stats.featuresPlanned}</span>
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-500/20 flex items-center justify-center">
                <Package className="h-6 w-6 text-green-500" />
              </div>
            </div>
            <p className="text-[10px] text-slate-500 mt-2">Complete / In Progress / Planned</p>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase">Automations</p>
                <p className="text-2xl font-bold text-purple-400">
                  {stats.activeAutomations} <span className="text-sm text-slate-500">active</span>
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                <Zap className="h-6 w-6 text-purple-500" />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">{stats.automationSuccessRate}% success rate</p>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase">Current Phase</p>
                <p className="text-lg font-bold text-orange-400 truncate">
                  {stats.currentPhase?.name?.replace('Phase ', '').split(':')[0] || 'None'}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                <Target className="h-6 w-6 text-orange-500" />
              </div>
            </div>
            {stats.currentPhase && (
              <Progress value={stats.currentPhase.progress} className="mt-2 h-1" />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="overview" className="gap-2 data-[state=active]:bg-cyan-500/20">
            <BarChart3 className="h-4 w-4" /> Overview
          </TabsTrigger>
          <TabsTrigger value="features" className="gap-2 data-[state=active]:bg-cyan-500/20">
            <Package className="h-4 w-4" /> Features ({features.length})
          </TabsTrigger>
          <TabsTrigger value="phases" className="gap-2 data-[state=active]:bg-cyan-500/20">
            <Layers className="h-4 w-4" /> Phases ({phases.length})
          </TabsTrigger>
          <TabsTrigger value="automations" className="gap-2 data-[state=active]:bg-cyan-500/20">
            <Zap className="h-4 w-4" /> Automations ({automations.length})
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Phase Timeline */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Layers className="h-4 w-4 text-orange-500" />
                  Build Phases
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {phases.sort((a, b) => (a.display_order || 0) - (b.display_order || 0)).map((phase, idx) => (
                    <div key={phase.id} className="flex items-center gap-3">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${
                        phase.status === 'complete' ? 'bg-green-500/20 text-green-400' :
                        phase.status === 'active' ? 'bg-orange-500/20 text-orange-400' :
                        'bg-slate-700 text-slate-500'
                      }`}>
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className={`text-sm ${phase.status === 'active' ? 'text-white font-medium' : 'text-slate-400'}`}>
                            {phase.name}
                          </span>
                          <span className="text-xs text-slate-500">{phase.progress}%</span>
                        </div>
                        <Progress value={phase.progress} className="h-1 mt-1" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recent Features */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Package className="h-4 w-4 text-green-500" />
                  In Progress Features
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[200px]">
                  <div className="space-y-2">
                    {features.filter(f => f.status === 'in_progress').map(feature => (
                      <div key={feature.id} className="flex items-center gap-2 p-2 bg-slate-800/50 rounded">
                        {getStatusIcon(feature.status)}
                        <span className="flex-1 text-sm truncate">{feature.name}</span>
                        <Badge className={`text-[10px] ${getPriorityBadge(feature.priority)}`}>
                          {feature.priority}
                        </Badge>
                      </div>
                    ))}
                    {features.filter(f => f.status === 'in_progress').length === 0 && (
                      <p className="text-sm text-slate-500 text-center py-4">No features in progress</p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Automations Status */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Zap className="h-4 w-4 text-purple-500" />
                Active Automations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-3">
                {automations.filter(a => a.status === 'active').map(automation => (
                  <div key={automation.id} className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
                    <div className="h-10 w-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                      <Zap className="h-5 w-5 text-purple-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{automation.name}</p>
                      <p className="text-xs text-slate-500">
                        {automation.total_runs || 0} runs • {automation.successful_runs || 0} success
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Features Tab */}
        <TabsContent value="features" className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              <Badge className="bg-green-500/20 text-green-400">{stats.featuresComplete} Complete</Badge>
              <Badge className="bg-yellow-500/20 text-yellow-400">{stats.featuresInProgress} In Progress</Badge>
              <Badge className="bg-slate-500/20 text-slate-400">{stats.featuresPlanned} Planned</Badge>
            </div>
            <Button 
              onClick={() => setFeatureDialog({ open: true, mode: 'create', data: null })}
              className="gap-2 bg-cyan-600 hover:bg-cyan-700"
            >
              <Plus className="h-4 w-4" /> Add Feature
            </Button>
          </div>

          <div className="grid gap-3">
            {features.sort((a, b) => {
              const priorityOrder = { P0: 0, P1: 1, P2: 2, P3: 3 };
              const statusOrder = { in_progress: 0, planned: 1, complete: 2, blocked: 3 };
              if (statusOrder[a.status] !== statusOrder[b.status]) {
                return statusOrder[a.status] - statusOrder[b.status];
              }
              return (priorityOrder[a.priority] || 2) - (priorityOrder[b.priority] || 2);
            }).map(feature => (
              <Card key={feature.id} className="bg-slate-900 border-slate-800">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {getStatusIcon(feature.status)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{feature.name}</span>
                        <Badge className={`text-[10px] ${getStatusBadge(feature.status)}`}>
                          {feature.status?.replace('_', ' ')}
                        </Badge>
                        <Badge className={`text-[10px] ${getPriorityBadge(feature.priority)}`}>
                          {feature.priority}
                        </Badge>
                      </div>
                      {feature.description && (
                        <p className="text-sm text-slate-400 mt-1">{feature.description}</p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setFeatureDialog({ open: true, mode: 'edit', data: feature })}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleFeatureDelete(feature.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Phases Tab */}
        <TabsContent value="phases" className="space-y-4">
          <div className="flex justify-end">
            <Button 
              onClick={() => setPhaseDialog({ open: true, mode: 'create', data: null })}
              className="gap-2 bg-cyan-600 hover:bg-cyan-700"
            >
              <Plus className="h-4 w-4" /> Add Phase
            </Button>
          </div>

          <div className="grid gap-4">
            {phases.map((phase, idx) => (
              <Card key={phase.id} className={`bg-slate-900 border-slate-800 ${
                phase.status === 'active' ? 'ring-2 ring-orange-500/50' : ''
              }`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className={`h-12 w-12 rounded-full flex items-center justify-center text-lg font-bold ${
                      phase.status === 'complete' ? 'bg-green-500/20 text-green-400' :
                      phase.status === 'active' ? 'bg-orange-500/20 text-orange-400' :
                      'bg-slate-700 text-slate-500'
                    }`}>
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-lg">{phase.name}</span>
                        <Badge className={`${getStatusBadge(phase.status)}`}>
                          {phase.status}
                        </Badge>
                      </div>
                      {phase.description && (
                        <p className="text-sm text-slate-400 mt-1">{phase.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-3">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-slate-500">Progress</span>
                            <span className="text-xs font-medium">{phase.progress}%</span>
                          </div>
                          <Progress value={phase.progress} className="h-2" />
                        </div>
                        {phase.target_date && (
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Target</p>
                            <p className="text-sm">{new Date(phase.target_date).toLocaleDateString()}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setPhaseDialog({ open: true, mode: 'edit', data: phase })}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Automations Tab */}
        <TabsContent value="automations" className="space-y-4">
          <div className="flex justify-end">
            <Button 
              onClick={() => setAutomationDialog({ open: true, mode: 'create', data: null })}
              className="gap-2 bg-cyan-600 hover:bg-cyan-700"
            >
              <Plus className="h-4 w-4" /> Add Automation
            </Button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {automations.map(automation => (
              <Card key={automation.id} className={`bg-slate-900 border-slate-800 ${
                automation.status === 'active' ? 'border-l-4 border-l-green-500' : ''
              }`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                      automation.status === 'active' ? 'bg-green-500/20' : 'bg-slate-700'
                    }`}>
                      <Zap className={`h-5 w-5 ${
                        automation.status === 'active' ? 'text-green-500' : 'text-slate-500'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{automation.name}</span>
                        <Badge className={`text-[10px] ${getStatusBadge(automation.status)}`}>
                          {automation.status}
                        </Badge>
                      </div>
                      {automation.description && (
                        <p className="text-sm text-slate-400 mt-1">{automation.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Settings className="h-3 w-3" />
                          {automation.trigger}
                        </span>
                        {automation.schedule && (
                          <span>{automation.schedule}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-2">
                        <span className="text-sm">
                          <span className="text-slate-500">Runs:</span> {automation.total_runs || 0}
                        </span>
                        <span className="text-sm">
                          <span className="text-slate-500">Success:</span>{' '}
                          <span className="text-green-400">{automation.successful_runs || 0}</span>
                        </span>
                        {automation.total_runs > 0 && (
                          <span className="text-sm">
                            <span className="text-slate-500">Rate:</span>{' '}
                            {Math.round((automation.successful_runs / automation.total_runs) * 100)}%
                          </span>
                        )}
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setAutomationDialog({ open: true, mode: 'edit', data: automation })}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Feature Dialog */}
      <FeatureDialog 
        open={featureDialog.open}
        mode={featureDialog.mode}
        data={featureDialog.data}
        phases={phases}
        onClose={() => setFeatureDialog({ open: false, mode: 'create', data: null })}
        onSave={handleFeatureSave}
      />

      {/* Phase Dialog */}
      <PhaseDialog 
        open={phaseDialog.open}
        mode={phaseDialog.mode}
        data={phaseDialog.data}
        onClose={() => setPhaseDialog({ open: false, mode: 'create', data: null })}
        onSave={handlePhaseSave}
      />

      {/* Automation Dialog */}
      <AutomationDialog 
        open={automationDialog.open}
        mode={automationDialog.mode}
        data={automationDialog.data}
        onClose={() => setAutomationDialog({ open: false, mode: 'create', data: null })}
        onSave={handleAutomationSave}
      />
      </div>
    </Layout>
  );
};

// Feature Dialog Component
const FeatureDialog = ({ open, mode, data, phases, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '', description: '', status: 'planned', priority: 'P2', phase_id: ''
  });

  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || '',
        description: data.description || '',
        status: data.status || 'planned',
        priority: data.priority || 'P2',
        phase_id: data.phase_id || ''
      });
    } else {
      setFormData({ name: '', description: '', status: 'planned', priority: 'P2', phase_id: '' });
    }
  }, [data, open]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Add Feature' : 'Edit Feature'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm text-slate-400">Name</label>
            <Input 
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="mt-1 bg-slate-800 border-slate-700"
              placeholder="Feature name"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400">Description</label>
            <Textarea 
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="mt-1 bg-slate-800 border-slate-700"
              placeholder="Brief description"
              rows={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-400">Status</label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger className="mt-1 bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">Planned</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="complete">Complete</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-slate-400">Priority</label>
              <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                <SelectTrigger className="mt-1 bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="P0">P0 - Critical</SelectItem>
                  <SelectItem value="P1">P1 - High</SelectItem>
                  <SelectItem value="P2">P2 - Medium</SelectItem>
                  <SelectItem value="P3">P3 - Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(formData)} className="bg-cyan-600 hover:bg-cyan-700">
            {mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Phase Dialog Component
const PhaseDialog = ({ open, mode, data, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '', description: '', status: 'planned', progress: 0, target_date: ''
  });

  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || '',
        description: data.description || '',
        status: data.status || 'planned',
        progress: data.progress || 0,
        target_date: data.target_date || ''
      });
    } else {
      setFormData({ name: '', description: '', status: 'planned', progress: 0, target_date: '' });
    }
  }, [data, open]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Add Phase' : 'Edit Phase'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm text-slate-400">Name</label>
            <Input 
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="mt-1 bg-slate-800 border-slate-700"
              placeholder="Phase name"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400">Description</label>
            <Textarea 
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="mt-1 bg-slate-800 border-slate-700"
              placeholder="What this phase covers"
              rows={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-400">Status</label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger className="mt-1 bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">Planned</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="complete">Complete</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-slate-400">Progress (%)</label>
              <Input 
                type="number"
                min="0"
                max="100"
                value={formData.progress}
                onChange={(e) => setFormData({ ...formData, progress: parseInt(e.target.value) || 0 })}
                className="mt-1 bg-slate-800 border-slate-700"
              />
            </div>
          </div>
          <div>
            <label className="text-sm text-slate-400">Target Date</label>
            <Input 
              type="date"
              value={formData.target_date}
              onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
              className="mt-1 bg-slate-800 border-slate-700"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(formData)} className="bg-cyan-600 hover:bg-cyan-700">
            {mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Automation Dialog Component
const AutomationDialog = ({ open, mode, data, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '', description: '', status: 'inactive', trigger: 'event', schedule: ''
  });

  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || '',
        description: data.description || '',
        status: data.status || 'inactive',
        trigger: data.trigger || 'event',
        schedule: data.schedule || ''
      });
    } else {
      setFormData({ name: '', description: '', status: 'inactive', trigger: 'event', schedule: '' });
    }
  }, [data, open]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Add Automation' : 'Edit Automation'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm text-slate-400">Name</label>
            <Input 
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="mt-1 bg-slate-800 border-slate-700"
              placeholder="Automation name"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400">Description</label>
            <Textarea 
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="mt-1 bg-slate-800 border-slate-700"
              placeholder="What this automation does"
              rows={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-400">Status</label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger className="mt-1 bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="testing">Testing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-slate-400">Trigger</label>
              <Select value={formData.trigger} onValueChange={(v) => setFormData({ ...formData, trigger: v })}>
                <SelectTrigger className="mt-1 bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="event">Event-based</SelectItem>
                  <SelectItem value="cron">Scheduled (Cron)</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {formData.trigger === 'cron' && (
            <div>
              <label className="text-sm text-slate-400">Schedule (Cron)</label>
              <Input 
                value={formData.schedule}
                onChange={(e) => setFormData({ ...formData, schedule: e.target.value })}
                className="mt-1 bg-slate-800 border-slate-700"
                placeholder="0 8 * * * (daily at 8am)"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(formData)} className="bg-cyan-600 hover:bg-cyan-700">
            {mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PlatformDashboard;
